import { useState } from 'react';

import { Button } from '@/components/Button';
import { ACCENT_COLORS, ColorPicker } from '@/components/ColorPicker';
import { Field } from '@/components/Field';
import { Segmented } from '@/components/Segmented';
import { Sheet } from '@/components/Sheet';
import { toast } from '@/lib/toast';
import type { BudgetPeriod, BudgetType, Client, ClientStatus } from './types';
import { useSaveClient } from './useClientAdmin';

const STATUSES: { value: ClientStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

const BUDGET_TYPES: { value: BudgetType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'retainer_hours', label: 'Hours' },
  { value: 'project_budget', label: 'Money' },
];

const PERIODS: { value: BudgetPeriod; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'one_off', label: 'One-off' },
];

interface Props {
  client: Client | null;
  onClose: () => void;
}

/**
 * Create or edit a client.
 *
 * Unlike the task sheet this one saves explicitly. A client is created rarely
 * and its fields interact -- a budget type without an amount is rejected by the
 * server -- so autosaving each keystroke would fire failing requests while the
 * form is still half-filled.
 */
export function ClientSheet({ client, onClose }: Props) {
  const save = useSaveClient();
  const editing = client !== null;

  const [name, setName] = useState(client?.name ?? '');
  const [color, setColor] = useState(client?.color ?? ACCENT_COLORS[0]!.value);
  const [status, setStatus] = useState<ClientStatus>(client?.status ?? 'active');
  const [contactName, setContactName] = useState(client?.contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(client?.contact_email ?? '');
  const [contactPhone, setContactPhone] = useState(client?.contact_phone ?? '');
  const [notes, setNotes] = useState(client?.notes ?? '');
  const [rate, setRate] = useState(client?.hourly_rate ?? '');
  const [currency, setCurrency] = useState(client?.currency ?? 'USD');
  const [budgetType, setBudgetType] = useState<BudgetType>(client?.budget_type ?? 'none');
  const [budgetAmount, setBudgetAmount] = useState(client?.budget_amount ?? '');
  const [budgetPeriod, setBudgetPeriod] = useState<BudgetPeriod>(
    client?.budget_period ?? 'monthly',
  );

  const [touched, setTouched] = useState(false);

  const nameError = touched && !name.trim() ? 'A client needs a name.' : undefined;
  const budgetError =
    touched && budgetType !== 'none' && !budgetAmount.trim()
      ? 'Set the budget amount, or choose None.'
      : undefined;

  const submit = () => {
    setTouched(true);
    if (!name.trim() || (budgetType !== 'none' && !budgetAmount.trim())) return;

    save.mutate(
      {
        ...(editing ? { id: client.id } : {}),
        name: name.trim(),
        color,
        status,
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        notes: notes.trim() || null,
        hourly_rate: rate.trim() || null,
        currency: currency.trim().toUpperCase() || 'USD',
        budget_type: budgetType,
        budget_amount: budgetType === 'none' ? null : budgetAmount.trim(),
        budget_period: budgetType === 'none' ? null : budgetPeriod,
      },
      {
        onSuccess: (saved) => {
          toast.show(editing ? `Saved ${saved.name}` : `Added ${saved.name}`);
          onClose();
        },
      },
    );
  };

  return (
    <Sheet
      title={editing ? 'Edit client' : 'New client'}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={save.isPending}>
            {editing ? 'Save' : 'Add client'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched(true)}
          error={nameError}
          // The Personal client is seeded and cannot be renamed away from its
          // role, but its label is the user's business.
          placeholder="Acme Corp"
        />

        <ColorPicker value={color} onChange={setColor} groupLabel="Client colour" />

        <Segmented label="Status" value={status} options={STATUSES} onChange={setStatus} />

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-small font-medium text-text-secondary">Billing</legend>

          <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-3">
            <Field
              label="Hourly rate"
              type="text"
              inputMode="decimal"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="150.00"
              hint="Leave blank for unbilled work."
              className="tabular"
            />
            <Field
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
              className="uppercase"
            />
          </div>

          <Segmented
            label="Budget"
            value={budgetType}
            options={BUDGET_TYPES}
            onChange={setBudgetType}
          />

          {budgetType !== 'none' && (
            <div className="flex flex-col gap-3">
              <Field
                label={budgetType === 'retainer_hours' ? 'Hours per period' : 'Amount per period'}
                inputMode="decimal"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e.target.value)}
                onBlur={() => setTouched(true)}
                error={budgetError}
                className="tabular"
              />
              <Segmented
                label="Resets"
                value={budgetPeriod}
                options={PERIODS}
                onChange={setBudgetPeriod}
              />
            </div>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-small font-medium text-text-secondary">Contact</legend>
          <Field label="Name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
          <Field
            label="Email"
            type="email"
            inputMode="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
          <Field
            label="Phone"
            type="tel"
            inputMode="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          />
        </fieldset>

        <div>
          <label htmlFor="client-notes" className="mb-2 block text-small font-medium text-text-secondary">
            Notes
          </label>
          <textarea
            id="client-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full resize-y rounded border border-border bg-surface-1 p-2 text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
          />
        </div>
      </div>
    </Sheet>
  );
}
